import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, Package, DollarSign, Users, RefreshCw, Search, Filter, ChevronLeft, ChevronRight, Eye, Check, X, Clock, Mail, Download } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LOCKER_LABELS = {
  small: 'S',
  medium: 'M',
  xlarge: 'XL'
};

const STATUS_COLORS = {
  pending: 'bg-yellow-500/20 text-yellow-500',
  confirmed: 'bg-green-500/20 text-green-500',
  completed: 'bg-blue-500/20 text-blue-500',
  cancelled: 'bg-red-500/20 text-red-500'
};

const PAYMENT_STATUS_COLORS = {
  pending: 'bg-yellow-500/20 text-yellow-500',
  paid: 'bg-green-500/20 text-green-500',
  failed: 'bg-red-500/20 text-red-500',
  expired: 'bg-gray-500/20 text-gray-500',
  refunded: 'bg-purple-500/20 text-purple-500'
};

const AdminLockerDashboard = () => {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [filterDate, setFilterDate] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [resendingEmail, setResendingEmail] = useState(null);
  const limit = 20;

  const fetchBookings = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/admin/locker-bookings?skip=${page * limit}&limit=${limit}`;
      if (filterDate) url += `&date=${filterDate}`;
      if (filterStatus) url += `&status=${filterStatus}`;
      
      const response = await axios.get(url, { withCredentials: true });
      setBookings(response.data.data || []);
      setTotal(response.data.total || 0);
      setError(null);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        navigate('/ub-gestione-2026');
      } else {
        setError('Errore nel caricamento delle prenotazioni.');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      // Get today's date
      const today = new Date().toISOString().split('T')[0];
      const todayBookings = bookings.filter(b => b.date === today);
      
      // Calculate stats from bookings
      const confirmedBookings = bookings.filter(b => b.status === 'confirmed' || b.payment_status === 'paid');
      const totalRevenue = confirmedBookings.reduce((sum, b) => sum + (b.total_price || 0), 0);
      const pendingBookings = bookings.filter(b => b.payment_status === 'pending');
      
      setStats({
        totalBookings: total,
        todayBookings: todayBookings.length,
        totalRevenue: totalRevenue,
        pendingPayments: pendingBookings.length
      });
    } catch (err) {
      console.error('Stats fetch error:', err);
      // Non-critical - dashboard still works without stats
    }
  };

  useEffect(() => {
    fetchBookings();
  }, [page, filterDate, filterStatus]);

  useEffect(() => {
    if (bookings.length > 0) {
      fetchStats();
    }
  }, [bookings, total]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const totalPages = Math.ceil(total / limit);

  const handleResendEmail = async (bookingId) => {
    setResendingEmail(bookingId);
    try {
      const response = await axios.post(`${API_URL}/api/admin/resend-email/${bookingId}`, {}, { withCredentials: true });
      alert(`✅ Email inviata!\n\nCodice: ${response.data.booking_code}\nUnlock Code: ${response.data.unlock_code}\nCliente: ${response.data.customer_email}`);
    } catch (err) {
      alert('❌ Errore invio email: ' + (err.response?.data?.detail || err.message));
    } finally {
      setResendingEmail(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] pt-24 pb-12 px-4 md:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading text-3xl font-bold text-white">Dashboard Prenotazioni</h1>
            <p className="text-gray-400">Gestisci le prenotazioni dei locker</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => navigate('/ub-gestione-2026/disponibilita')}
              className="flex items-center gap-2 bg-[#141414] border border-[#FFCC00] hover:bg-[#FFCC00] hover:text-[#0A0A0A] text-[#FFCC00] font-bold px-4 py-2 transition-colors"
              data-testid="availability-btn"
            >
              <Calendar className="w-4 h-4" />
              Gestione Disponibilità
            </button>
            <a
              href={`${API_URL}/api/admin/locker-bookings/export${filterDate ? `?date_from=${filterDate}&date_to=${filterDate}` : ''}${filterStatus ? `${filterDate ? '&' : '?'}status=${filterStatus}` : ''}`}
              className="flex items-center gap-2 bg-[#FFCC00] hover:bg-[#E6B800] text-[#0A0A0A] font-bold px-4 py-2 transition-colors"
              data-testid="export-csv-btn"
            >
              <Download className="w-4 h-4" />
              Esporta CSV
            </a>
            <button
              onClick={fetchBookings}
              className="flex items-center gap-2 bg-[#141414] border border-[#2A2A2A] hover:border-[#FFCC00] text-white px-4 py-2 transition-colors"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Aggiorna
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-[#141414] border border-[#2A2A2A] p-6">
              <div className="flex items-center gap-3 mb-2">
                <Package className="w-5 h-5 text-[#FFCC00]" />
                <span className="text-gray-400 text-sm">Totale Prenotazioni</span>
              </div>
              <p className="text-white text-3xl font-bold">{stats.totalBookings}</p>
            </div>
            <div className="bg-[#141414] border border-[#2A2A2A] p-6">
              <div className="flex items-center gap-3 mb-2">
                <Calendar className="w-5 h-5 text-[#FFCC00]" />
                <span className="text-gray-400 text-sm">Oggi</span>
              </div>
              <p className="text-white text-3xl font-bold">{stats.todayBookings}</p>
            </div>
            <div className="bg-[#141414] border border-[#2A2A2A] p-6">
              <div className="flex items-center gap-3 mb-2">
                <DollarSign className="w-5 h-5 text-[#FFCC00]" />
                <span className="text-gray-400 text-sm">Ricavi</span>
              </div>
              <p className="text-white text-3xl font-bold">€{stats.totalRevenue.toFixed(2)}</p>
            </div>
            <div className="bg-[#141414] border border-[#2A2A2A] p-6">
              <div className="flex items-center gap-3 mb-2">
                <Clock className="w-5 h-5 text-yellow-500" />
                <span className="text-gray-400 text-sm">In Attesa</span>
              </div>
              <p className="text-white text-3xl font-bold">{stats.pendingPayments}</p>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="flex items-center gap-2 bg-[#141414] border border-[#2A2A2A] px-4 py-2">
            <Calendar className="w-4 h-4 text-gray-400" />
            <input
              type="date"
              value={filterDate}
              onChange={(e) => { setFilterDate(e.target.value); setPage(0); }}
              className="bg-transparent text-white outline-none"
            />
            {filterDate && (
              <button onClick={() => setFilterDate('')} className="text-gray-400 hover:text-white">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <div className="flex items-center gap-2 bg-[#141414] border border-[#2A2A2A] px-4 py-2">
            <Filter className="w-4 h-4 text-gray-400" />
            <select
              value={filterStatus}
              onChange={(e) => { setFilterStatus(e.target.value); setPage(0); }}
              className="bg-transparent text-white outline-none"
            >
              <option value="" className="bg-[#141414]">Tutti gli stati</option>
              <option value="pending" className="bg-[#141414]">In attesa</option>
              <option value="confirmed" className="bg-[#141414]">Confermato</option>
              <option value="completed" className="bg-[#141414]">Completato</option>
              <option value="cancelled" className="bg-[#141414]">Annullato</option>
            </select>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-500/20 border border-red-500/50 text-red-400 p-4 mb-6">
            {error}
          </div>
        )}

        {/* Table */}
        <div className="bg-[#141414] border border-[#2A2A2A] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#2A2A2A]">
                  <th className="text-left text-gray-400 text-xs uppercase tracking-wider px-4 py-3">Codice</th>
                  <th className="text-left text-gray-400 text-xs uppercase tracking-wider px-4 py-3">Cliente</th>
                  <th className="text-left text-gray-400 text-xs uppercase tracking-wider px-4 py-3">Data</th>
                  <th className="text-left text-gray-400 text-xs uppercase tracking-wider px-4 py-3">Orario</th>
                  <th className="text-left text-gray-400 text-xs uppercase tracking-wider px-4 py-3">Locker</th>
                  <th className="text-left text-gray-400 text-xs uppercase tracking-wider px-4 py-3">Totale</th>
                  <th className="text-left text-gray-400 text-xs uppercase tracking-wider px-4 py-3">Pagamento</th>
                  <th className="text-left text-gray-400 text-xs uppercase tracking-wider px-4 py-3">Stato</th>
                  <th className="text-left text-gray-400 text-xs uppercase tracking-wider px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={9} className="text-center py-12 text-gray-400">
                      Caricamento...
                    </td>
                  </tr>
                ) : bookings.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="text-center py-12 text-gray-400">
                      Nessuna prenotazione trovata
                    </td>
                  </tr>
                ) : (
                  bookings.map((booking) => (
                    <tr key={booking.id} className="border-b border-[#2A2A2A] hover:bg-[#1A1A1A]">
                      <td className="px-4 py-3">
                        <span className="text-[#FFCC00] font-mono font-bold">{booking.booking_code}</span>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-white text-sm">{booking.name}</p>
                        <p className="text-gray-500 text-xs">{booking.email}</p>
                      </td>
                      <td className="px-4 py-3 text-white text-sm">{booking.date}</td>
                      <td className="px-4 py-3 text-white text-sm">{booking.start_time} - {booking.end_time}</td>
                      <td className="px-4 py-3">
                        <span className="bg-[#2A2A2A] text-white text-xs px-2 py-1">
                          {booking.lockers_summary || 
                           (booking.quantity > 1 
                             ? `${booking.quantity}× ${LOCKER_LABELS[booking.locker_size] || booking.locker_size}` 
                             : LOCKER_LABELS[booking.locker_size] || booking.locker_size)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-white font-semibold">€{booking.total_price?.toFixed(2)}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-1 ${PAYMENT_STATUS_COLORS[booking.payment_status] || 'bg-gray-500/20 text-gray-500'}`}>
                          {booking.payment_status?.toUpperCase() || 'N/A'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-1 ${STATUS_COLORS[booking.status] || 'bg-gray-500/20 text-gray-500'}`}>
                          {booking.status?.toUpperCase() || 'N/A'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => setSelectedBooking(booking)}
                          className="text-gray-400 hover:text-[#FFCC00] transition-colors"
                          title="Dettagli"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleResendEmail(booking.id)}
                          disabled={resendingEmail === booking.id}
                          className="text-gray-400 hover:text-green-500 transition-colors disabled:opacity-50"
                          title="Re-invia Email"
                        >
                          <Mail className={`w-4 h-4 ${resendingEmail === booking.id ? 'animate-pulse' : ''}`} />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-[#2A2A2A]">
              <p className="text-gray-400 text-sm">
                Pagina {page + 1} di {totalPages} ({total} totali)
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(Math.max(0, page - 1))}
                  disabled={page === 0}
                  className="p-2 text-gray-400 hover:text-white disabled:opacity-30"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
                  disabled={page >= totalPages - 1}
                  className="p-2 text-gray-400 hover:text-white disabled:opacity-30"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Booking Detail Modal */}
        {selectedBooking && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80" onClick={() => setSelectedBooking(null)}>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-[#141414] border border-[#2A2A2A] w-full max-w-md p-6"
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-white font-bold text-lg">Dettagli Prenotazione</h3>
                <button onClick={() => setSelectedBooking(null)} className="text-gray-400 hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="bg-[#0A0A0A] border border-[#FFCC00] p-4 text-center mb-4">
                <p className="text-gray-400 text-sm">Codice</p>
                <p className="text-[#FFCC00] text-2xl font-mono font-bold">{selectedBooking.booking_code}</p>
              </div>

              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-400">Nome:</span>
                  <span className="text-white">{selectedBooking.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Email:</span>
                  <span className="text-white">{selectedBooking.email}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Telefono:</span>
                  <span className="text-white">{selectedBooking.phone}</span>
                </div>
                <div className="border-t border-[#2A2A2A] pt-3"></div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Data:</span>
                  <span className="text-white">{selectedBooking.date}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Orario:</span>
                  <span className="text-white">{selectedBooking.start_time} - {selectedBooking.end_time}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Locker:</span>
                  <span className="text-white">
                    {selectedBooking.lockers_summary || 
                     (selectedBooking.quantity > 1 
                       ? `${selectedBooking.quantity}× ${LOCKER_LABELS[selectedBooking.locker_size]} (${selectedBooking.locker_size})` 
                       : `${LOCKER_LABELS[selectedBooking.locker_size]} (${selectedBooking.locker_size})`)}
                  </span>
                </div>
                {selectedBooking.lockers && (
                  <div className="bg-[#1A1A1A] p-3 rounded mt-2">
                    <span className="text-gray-400 text-sm">Dettaglio locker:</span>
                    <div className="mt-1 text-white text-sm">
                      {Object.entries(selectedBooking.lockers).map(([size, qty]) => 
                        qty > 0 && (
                          <div key={size}>{qty}× {LOCKER_LABELS[size] || size}</div>
                        )
                      )}
                    </div>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-400">Durata:</span>
                  <span className="text-white">{selectedBooking.hours} ore</span>
                </div>
                <div className="border-t border-[#2A2A2A] pt-3"></div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Pagamento:</span>
                  <span className={`px-2 py-0.5 ${PAYMENT_STATUS_COLORS[selectedBooking.payment_status]}`}>
                    {selectedBooking.payment_status?.toUpperCase()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Stato:</span>
                  <span className={`px-2 py-0.5 ${STATUS_COLORS[selectedBooking.status]}`}>
                    {selectedBooking.status?.toUpperCase()}
                  </span>
                </div>
                <div className="flex justify-between items-center border-t border-[#2A2A2A] pt-3">
                  <span className="text-gray-400 font-semibold">Totale:</span>
                  <span className="text-[#FFCC00] text-xl font-bold">€{selectedBooking.total_price?.toFixed(2)}</span>
                </div>
                
                {/* Codice Promo da copiare */}
                <div className="mt-4 bg-[#FFCC00] p-4 text-center">
                  <span className="text-[#0A0A0A] text-xs font-bold block mb-1">CODICE PROMO (da copiare nel portale)</span>
                  <span className="text-[#0A0A0A] text-3xl font-mono font-bold tracking-wider">{selectedBooking.unlock_code}</span>
                </div>
              </div>

              <div className="mt-6 flex gap-3">
                <button
                  onClick={() => handleResendEmail(selectedBooking.id)}
                  disabled={resendingEmail === selectedBooking.id}
                  className="flex-1 bg-[#FFCC00] text-[#0A0A0A] font-bold py-2 hover:bg-[#E6B800] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  <Mail className={`w-4 h-4 ${resendingEmail === selectedBooking.id ? 'animate-pulse' : ''}`} />
                  {resendingEmail === selectedBooking.id ? 'Invio...' : 'Re-invia Email'}
                </button>
                <a
                  href={`https://wa.me/39${selectedBooking.phone?.replace(/\D/g, '')}?text=Ciao ${selectedBooking.name}, riguardo la tua prenotazione ${selectedBooking.booking_code}...`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 bg-[#25D366] text-white text-center py-2 hover:bg-[#20BD5A] transition-colors flex items-center justify-center"
                >
                  WhatsApp
                </a>
              </div>
              <button
                onClick={() => setSelectedBooking(null)}
                className="w-full mt-3 bg-[#2A2A2A] text-white py-2 hover:bg-[#3A3A3A] transition-colors"
              >
                Chiudi
              </button>
            </motion.div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminLockerDashboard;
